import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InlineFeedback from 'components/forms/molecules/InlineFeedback';
import useCurrentUser from 'hooks/useCurrentUser';
import type React from 'react';
import { type FC, useState } from 'react';
import type { PromptSectionDraft } from 'services/types/promptSection';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';
import { getMutationErrorMessage } from 'utils/mutationError';

export interface PromptSectionSaveOptions {
  activatePromptVersion: boolean;
}

interface Props {
  close: () => void;
  title?: string;
  promptSection: PromptSectionDraft;
  save?: (promptSection: PromptSectionDraft, options: PromptSectionSaveOptions) => Promise<void> | void;
  createsPromptVersion?: boolean;
}

const PromptSectionModal: FC<Props> = ({
  close,
  title = 'Editar seccion',
  promptSection,
  save,
  createsPromptVersion = true,
}) => {
  const currentUser = useCurrentUser();
  const [name, setName] = useState<string>(promptSection.name);
  const [displayName, setDisplayName] = useState<string>(promptSection.displayName);
  const [value, setValue] = useState<string>(promptSection.value);
  const [permission, setPermission] = useState(promptSection.permission);
  const [confirmingVersion, setConfirmingVersion] = useState(false);
  const [activatePromptVersion, setActivatePromptVersion] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value.replace(/[^a-zA-Z0-9]/g, '_'));
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: RADIUS_ITEM,
      bgcolor: SURFACE_CONTAINER_LOW,
      fontFamily: 'Jost, sans-serif',
      '& fieldset': { borderColor: 'transparent' },
      '&.Mui-focused fieldset': { borderColor: NAVY, borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root': { fontFamily: 'Jost, sans-serif' },
  };

  return (
    <Modal open onClose={() => close()}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '92%', sm: '80%', md: '60%', lg: '50%' },
          maxHeight: '90vh',
          overflow: 'auto',
          borderRadius: RADIUS_CARD,
          bgcolor: SURFACE_CONTAINER_LOWEST,
          boxShadow: SHADOW,
        }}
      >
        <Stack spacing={3} sx={{ p: { xs: 3, md: 4 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: RADIUS_ITEM,
                bgcolor: NAVY,
                color: '#fff',
                flexShrink: 0,
              }}
            >
              <AddIcon />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: NAVY, fontFamily: 'Jost, sans-serif' }}>
              {title}
            </Typography>
          </Box>

          {confirmingVersion && createsPromptVersion ? (
            <Stack spacing={2.5}>
              <Typography sx={{ color: NAVY, fontFamily: 'Jost, sans-serif', lineHeight: 1.6 }}>
                Guardar esta sección creará una nueva versión del prompt con el snapshot actualizado de sus secciones.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={activatePromptVersion}
                    onChange={(event) => setActivatePromptVersion(event.target.checked)}
                    sx={{ color: NAVY, '&.Mui-checked': { color: NAVY } }}
                  />
                }
                label="Activar esta nueva versión ahora"
                sx={{ color: NAVY, fontFamily: 'Jost, sans-serif' }}
              />
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              <TextField
                value={name}
                onChange={handleNameChange}
                label="Título"
                placeholder="(No se puede cambiar una vez creado)"
                disabled={!!promptSection.name}
                fullWidth
                sx={inputSx}
              />
              <TextField
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                label="Descripcion"
                placeholder="Nombre/descripcion de esta seccion"
                fullWidth
                sx={inputSx}
              />
              <TextField
                value={value}
                onChange={(e) => setValue(e.target.value)}
                label="Valor"
                multiline
                maxRows={10}
                fullWidth
                sx={inputSx}
              />
              <Select
                value={permission}
                onChange={(e) => setPermission(e.target.value as 'hidden' | 'read-only' | 'suggest-edit' | 'editable')}
                label="Permiso"
                disabled={!currentUser?.isAdmin}
                sx={{
                  borderRadius: RADIUS_ITEM,
                  bgcolor: SURFACE_CONTAINER_LOW,
                  fontFamily: 'Jost, sans-serif',
                  '& fieldset': { borderColor: 'transparent' },
                  '&.Mui-focused fieldset': { borderColor: NAVY, borderWidth: '1.5px' },
                }}
              >
                <MenuItem value="hidden">Oculto</MenuItem>
                <MenuItem value="read-only">Solo lectura</MenuItem>
                <MenuItem value="suggest-edit">Sugerir edicion</MenuItem>
                <MenuItem value="editable">Editable</MenuItem>
              </Select>
            </Stack>
          )}

          {saveError && <InlineFeedback severity="error">{saveError}</InlineFeedback>}

          {/* Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, pt: 1 }}>
            <Button
              onClick={() => (confirmingVersion && createsPromptVersion ? setConfirmingVersion(false) : close())}
              variant="outlined"
              sx={{
                borderRadius: RADIUS_COIN,
                textTransform: 'none',
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                color: NAVY,
                borderColor: `rgba(21, 24, 58, 0.15)`,
                '&:hover': { borderColor: NAVY },
              }}
            >
              {confirmingVersion && createsPromptVersion ? 'Volver' : 'Cancelar'}
            </Button>
            <Button
              disabled={!name || !displayName || !value}
              onClick={() => {
                if (createsPromptVersion && !confirmingVersion) {
                  setConfirmingVersion(true);
                  return;
                }
                setIsSaving(true);
                setSaveError(null);
                Promise.resolve(
                  save?.({ ...promptSection, name, displayName, value, permission }, { activatePromptVersion })
                )
                  .then(close)
                  .catch((error) => setSaveError(getMutationErrorMessage(error, 'No fue posible guardar la sección.')))
                  .finally(() => setIsSaving(false));
              }}
              variant="contained"
              sx={{
                borderRadius: RADIUS_COIN,
                textTransform: 'none',
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                bgcolor: YELLOW,
                color: NAVY,
                '&:hover': { bgcolor: YELLOW, opacity: 0.85 },
                '&.Mui-disabled': { bgcolor: `rgba(21, 24, 58, 0.08)`, color: `rgba(21, 24, 58, 0.3)` },
              }}
            >
              {isSaving ? 'Guardando…' : confirmingVersion && createsPromptVersion ? 'Guardar versión' : 'Guardar'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};

export default PromptSectionModal;
